/**
 * Email Utilities
 *
 * Functions for rendering React Email templates to HTML and copying to clipboard.
 * Enables the workflow: Generate Email → Copy to Clipboard → Paste in Email Client → Send
 */

import { render } from '@react-email/render';
import type { ReactElement } from 'react';

// ============================================
// Render Functions
// ============================================

/**
 * Render a React Email template to email-safe HTML string.
 *
 * @param template - React element (email template)
 * @returns Promise resolving to HTML string
 *
 * @example
 * const html = await renderEmailToHtml(
 *   <DataReportEmail
 *     recipientName="John"
 *     subject="Monthly Report"
 *     ...
 *   />
 * );
 */
export async function renderEmailToHtml(template: ReactElement): Promise<string> {
  return await render(template, {
    pretty: true, // Formatted HTML for readability
  });
}

/**
 * Render email template to plain text version (for multipart emails)
 */
export async function renderEmailToText(template: ReactElement): Promise<string> {
  return await render(template, {
    plainText: true,
  });
}

// ============================================
// Clipboard Functions
// ============================================

/**
 * Copy HTML content to clipboard as both HTML and plain text.
 * When pasted into email clients, they'll use the HTML version.
 *
 * @param html - HTML string to copy
 * @returns Promise resolving when copied
 *
 * @example
 * await copyEmailToClipboard(html);
 * // Now paste with Cmd+V in Outlook/Gmail
 */
export async function copyEmailToClipboard(html: string): Promise<void> {
  // Try to use the modern Clipboard API with HTML support
  if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
    try {
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([stripHtml(html)], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob,
      });

      await navigator.clipboard.write([clipboardItem]);
      return;
    } catch {
      // Fall through to fallback
    }
  }

  // Fallback: Try writeText if clipboard exists
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(html);
    return;
  }

  // Last resort fallback: Use execCommand (deprecated but widely supported)
  const textarea = document.createElement('textarea');
  textarea.value = html;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

/**
 * Copy only plain text to clipboard
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

// ============================================
// Helper Functions
// ============================================

/**
 * Strip HTML tags to get plain text
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Generate email and copy to clipboard in one step
 *
 * @param template - React email template element
 * @returns Promise resolving to the generated HTML
 *
 * @example
 * const html = await generateAndCopyEmail(
 *   <MetricsAlertEmail ... />
 * );
 * // Email is now in clipboard, ready to paste
 */
export async function generateAndCopyEmail(template: ReactElement): Promise<string> {
  const html = await renderEmailToHtml(template);
  await copyEmailToClipboard(html);
  return html;
}

// ============================================
// Preview Functions
// ============================================

/**
 * Open email preview in a new window/tab
 * Useful for reviewing the email before copying
 */
export function previewEmailInNewWindow(html: string, title = 'Email Preview'): Window | null {
  const previewWindow = window.open('', '_blank');
  if (previewWindow) {
    previewWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { margin: 0; padding: 20px; background: #f0f0f0; }
          </style>
        </head>
        <body>
          ${html}
        </body>
      </html>
    `);
    previewWindow.document.close();
  }
  return previewWindow;
}

/**
 * Generate a data URL for iframe preview
 */
export function getEmailPreviewDataUrl(html: string): string {
  const fullHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body { margin: 0; padding: 20px; background: #f6f9fc; }
        </style>
      </head>
      <body>
        ${html}
      </body>
    </html>
  `;
  return `data:text/html;charset=utf-8,${encodeURIComponent(fullHtml)}`;
}

// ============================================
// Validation
// ============================================

/**
 * Check if an image is a valid base64 data URL
 */
export function isValidBase64Image(src: string): boolean {
  return src.startsWith('data:image/') && src.includes('base64,');
}

/**
 * Estimate email size in bytes
 * Useful for checking if embedded images make the email too large
 */
export function estimateEmailSize(html: string): number {
  return new Blob([html]).size;
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default {
  renderEmailToHtml,
  renderEmailToText,
  copyEmailToClipboard,
  copyTextToClipboard,
  generateAndCopyEmail,
  previewEmailInNewWindow,
  getEmailPreviewDataUrl,
  isValidBase64Image,
  estimateEmailSize,
  formatBytes,
};
